import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Quote, ArrowRight } from "lucide-react";

import { PageHero } from "../components/site/PageHero";
import { GoogleReviewsBadge } from "../components/site/GoogleReviewsBadge";
import { usePageMeta } from "../hooks/usePageMeta";

const REVIEWS = [
  { rating: 5, text: "Geweldige service en altijd perfect schoon. Het team is vriendelijk, stipt en denkt mee. Zeker een aanrader voor elk kantoor!", author: "Marie Peeters", role: "Office Manager", area: "Antwerpen-Zuid" },
  { rating: 5, text: "Betrouwbaar, snel en 24/7 bereikbaar. Na de opleverings­schoonmaak van ons nieuwe pand was alles spotless.", author: "Jan S.", role: "Zaakvoerder", area: "Berchem" },
  { rating: 5, text: "Onze ramen blinken al maanden. Vakwerk en altijd correcte communicatie. Tot in de details verzorgd.", author: "Lieselot V.", role: "Particulier", area: "Eilandje" },
  { rating: 5, text: "Discreet, snel en bijzonder grondig. We hebben Dila Group BV ondertussen vast in dienst genomen voor ons restaurant.", author: "Karim B.", role: "Restauranthouder", area: "Het Zuid" },
  { rating: 5, text: "Ik werk in shifts en de flexibiliteit van het team is goud waard. Ze passen zich perfect aan mijn schema aan.", author: "Sofie D.", role: "Verpleegkundige", area: "Borgerhout" },
  { rating: 5, text: "Na een grondige verbouwing was ons huis een ramp. Eén dieptereiniging later konden we erin trekken. Top!", author: "Tom & Hannah", role: "Particulieren", area: "Deurne" },
  { rating: 5, text: "Wekelijkse kantoorschoonmaak voor onze advocatenpraktijk. Vast team, vaste afspraken, nooit een probleem.", author: "Mr. Vermeulen", role: "Advocaat", area: "Antwerpen-Centrum" },
  { rating: 4, text: "Heel tevreden van de service. Eén keer kwam de poetshulp wat later door verkeer, maar duidelijke communicatie. Verder top.", author: "Emine Y.", role: "Particulier", area: "Wilrijk" },
];

const Stars = ({ count = 5 }) => (
  <div className="flex items-center gap-1 text-[#F59E0B]">
    {Array.from({ length: 5 }).map((_, j) => (
      <Star
        key={j}
        size={16}
        fill="currentColor"
        strokeWidth={0}
        className={j < count ? "" : "text-slate-200"}
      />
    ))}
  </div>
);

export default function ReviewsPage() {
  usePageMeta({
    title: "Reviews — Klantbeoordelingen Dila Group BV | 4.9 / 5 ★",
    description:
      "Echte ervaringen van klanten met Dila Group BV in Antwerpen. 4.9/5 sterren op basis van 87+ Google reviews. Lees wat particulieren en bedrijven over ons zeggen.",
    path: "/reviews",
  });

  const avg = (REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length).toFixed(1);

  return (
    <>
      <PageHero
        breadcrumb="Reviews"
        eyebrow="Klantbeoordelingen"
        title="Wat onze klanten over ons zeggen."
        intro="Een gemiddelde van 4.9 op 5 sterren is het resultaat van consequent vakwerk en eerlijke communicatie. Hieronder een selectie van echte ervaringen."
      />

      {/* Rating overview */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="reviews-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12 text-center sm:text-left">
          <div>
            <p className="font-display text-6xl font-extrabold text-[#0F172A] leading-none">{avg}</p>
            <Stars count={5} />
            <p className="text-xs text-slate-500 mt-2">Gemiddelde over {REVIEWS.length} reviews op deze pagina</p>
          </div>
          <div className="hidden sm:block h-16 w-px bg-slate-200" />
          <GoogleReviewsBadge compact />
        </div>
      </section>

      {/* Reviews grid */}
      <section className="py-20 sm:py-28 bg-[#F4F7F6]" data-testid="reviews-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REVIEWS.map((r, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={`review-${i}`}
                className="relative rounded-3xl bg-white border border-slate-200 p-7 hover:border-[#0284C7]/40 hover:shadow-lg transition-all"
              >
                <Quote className="absolute top-5 right-5 text-[#0284C7]/15" size={44} />
                <Stars count={r.rating} />
                <blockquote className="mt-4 font-quote text-lg text-[#0F172A] leading-snug italic">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#0284C7] to-[#16A34A] text-white flex items-center justify-center font-display font-bold">
                    {r.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-display font-bold text-sm text-[#0F172A]">{r.author}</p>
                    <p className="text-xs text-slate-500">{r.role} · {r.area}</p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-testid="reviews-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] text-balance">
            Klaar om de volgende tevreden klant te worden?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Vraag uw vrijblijvende offerte aan en ervaar zelf wat onze klanten zo waarderen.
          </p>
          <Link
            to="/contact"
            data-testid="reviews-cta-quote"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-7 py-4 shadow-lg shadow-sky-200 transition-all hover:-translate-y-0.5"
          >
            Offerte aanvragen <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
